import React from "react";
import { Card, Badge, Button, Dropdown, Form } from "react-bootstrap";
import {
  Edit,
  Trash2,
  Eye,
  Package,
  AlertTriangle,
  CheckCircle,
  MoreVertical,
  Printer,
  Tag,
} from "lucide-react";
import ProductImage from "./ProductImage";
import { PRODUCT_TYPES } from "../../services/productService";

const ProductCard = ({
  product,
  onEdit,
  onDelete,
  onView,
  isManager = false,
  formatPrice,
  getStockBadgeVariant,
  // Nouvelles props pour l'étiquetage
  showCheckbox = false,
  isSelected = false,
  onSelect,
  onGenerateLabel,
}) => {
  const {
    id,
    name,
    productType,
    description,
    price,
    categoryName,
    currentStock,
    stock,
    isActive,
    isLowStock,
    maxThreshold,
    minThreshold,
    
    sku,
    barcode,
    // URL d'image
    imageUrl,
    image_url,
    image,
    
    // ✅ NOUVEAU : Champs promotions
    hasPromo,
    promoPrice,
    discountPercentage,
    activePromotionName,
  } = product;

  // Fonctions utilitaires
  const formatPriceLocal = (price) => {
    if (formatPrice) {
      return formatPrice(price);
    }
    if (!price) return "0 FCFA";
    return (
      new Intl.NumberFormat("fr-FR", {
        style: "decimal",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(price) + " FCFA"
    );
  };

  const isService = productType === PRODUCT_TYPES.SERVICE;

  const getStockBadge = (stockValue) => {
    if (isService) {
      return <Badge bg="success">Disponible</Badge>;
    }

    const stockCount = stockValue || currentStock || stock || 0;

    if (!stockCount || stockCount === 0) {
      return <Badge bg="danger">Rupture</Badge>;
    } else if (stockCount <= 5 || isLowStock) {
      return <Badge bg="warning">Stock faible ({stockCount})</Badge>;
    } else {
      return <Badge bg="success">En stock ({stockCount})</Badge>;
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge bg="success" size="sm">
        <CheckCircle size={12} className="me-1" />
        Actif
      </Badge>
    ) : (
      <Badge bg="secondary" size="sm">
        <AlertTriangle size={12} className="me-1" />
        Inactif
      </Badge>
    );
  };

  // Fonctions pour l'étiquetage
  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    onSelect?.(e.target.checked);
  };

  const handleGenerateLabel = (e) => {
    e.stopPropagation();
    onGenerateLabel?.();
  };

  return (
    <Card
      className={`product-card h-100 shadow-sm ${
        isSelected ? "border-primary" : ""
      }`}
    >
      {/* Checkbox pour sélection multiple */}
      {showCheckbox && (
        <div
          className="position-absolute top-0 start-0 p-2"
          style={{ zIndex: 10 }}
        >
          <Form.Check
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            className="bg-white rounded p-1 shadow-sm"
            style={{
              transform: "scale(1.2)",
              accentColor: "#0d6efd",
            }}
          />
        </div>
      )}

      {/* ✅ NOUVEAU : Badge PROMO en haut à gauche */}
      {hasPromo && (
        <div
          className="position-absolute top-0 start-0 p-2"
          style={{ zIndex: 15, marginLeft: showCheckbox ? '30px' : '0' }}
        >
          <Badge 
            bg="danger" 
            className="d-flex align-items-center gap-1 px-2 py-1"
            style={{ fontSize: "0.75rem" }}
          >
            <Tag size={12} />
            {discountPercentage ? `-${Math.round(discountPercentage)}%` : 'PROMO'}
          </Badge>
        </div>
      )}

      {/* SECTION IMAGE */}
      <div
        className="product-image-container"
        style={{
          height: "200px",
          position: "relative",
          borderRadius: "0.375rem 0.375rem 0 0",
          overflow: "hidden",
        }}
      >
        <ProductImage
          product={product}
          size="card"
          className="w-100 h-100"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "0",
          }}
          showPlaceholder={true}
        />

        {/* Actions overlay */}
        <div
          className="position-absolute top-0 end-0 p-2"
          style={{ zIndex: 10 }}
        >
          <Dropdown>
            <Dropdown.Toggle
              variant="light"
              size="sm"
              className="border-0 shadow-sm dropdown-toggle-custom"
              style={{
                opacity: 0.9,
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(2px)",
              }}
            >
              <MoreVertical size={16} />
            </Dropdown.Toggle>

            <Dropdown.Menu
              align="end"
              className="shadow-lg border-0 compact-dropdown"
              style={{
                borderRadius: "8px",
                minWidth: "180px",
                zIndex: 1050,
                padding: "0.25rem 0",
              }}
            >
              <Dropdown.Item
                onClick={() => onView?.(product)}
                className="py-1 px-3"
                style={{ fontSize: "0.875rem", lineHeight: "1.2" }}
              >
                <Eye size={13} className="me-2" />
                Voir détails
              </Dropdown.Item>

              {barcode && (
                <>
                  <Dropdown.Divider className="my-1" />
                  <Dropdown.Item
                    onClick={handleGenerateLabel}
                    className="py-1 px-3"
                    style={{ fontSize: "0.875rem", lineHeight: "1.2" }}
                  >
                    <Printer size={13} className="me-2" />
                    Générer étiquette
                  </Dropdown.Item>
                </>
              )}

              {isManager && (
                <>
                  <Dropdown.Divider className="my-1" />
                  <Dropdown.Item
                    onClick={() => onEdit?.(product)}
                    className="py-1 px-3"
                    style={{ fontSize: "0.875rem", lineHeight: "1.2" }}
                  >
                    <Edit size={13} className="me-2" />
                    Modifier
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => onDelete?.(product)}
                    className="text-danger py-1 px-3"
                    style={{ fontSize: "0.875rem", lineHeight: "1.2" }}
                  >
                    <Trash2 size={13} className="me-2" />
                    Supprimer
                  </Dropdown.Item>
                </>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {/* Indicateur de code-barres */}
        {barcode && (
          <div className="position-absolute bottom-0 end-0 p-2">
            <Badge bg="secondary" className="small">
              <Printer size={10} className="me-1" />
              Code-barres
            </Badge>
          </div>
        )}
      </div>

      {/* Card Body */}
      <Card.Body className="d-flex flex-column">
        {/* En-tête avec catégorie */}
        <div className="product-header mb-2">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <small className="text-muted">
              {categoryName || "Sans catégorie"}
            </small>
            {getStockBadge(currentStock || stock)}
          </div>

          {/* Affichage des seuils si disponibles */}
          {!isService && (minThreshold || maxThreshold) && (
            <div className="stock-thresholds">
              <small className="text-muted d-block">
                {minThreshold !== undefined && (
                  <span className="me-2">
                    <span className="fw-semibold">Min:</span> {minThreshold}
                  </span>
                )}
                {maxThreshold !== undefined && maxThreshold !== null && (
                  <span>
                    <span className="fw-semibold">Max:</span> {maxThreshold}
                  </span>
                )}
              </small>
            </div>
          )}
        </div>

        {/* Nom du produit */}
        <Card.Title
          className="product-name mb-2"
          style={{ fontSize: "1.1rem", lineHeight: "1.3", cursor: "pointer" }}
          title={name}
          onClick={() => onView?.(product)}
        >
          {name && name.length > 40 ? `${name.substring(0, 40)}...` : name}
        </Card.Title>

        {/* Description */}
        {description && (
          <Card.Text className="product-description text-muted small mb-2">
            {description.length > 80
              ? `${description.substring(0, 80)}...`
              : description}
          </Card.Text>
        )}

        {/* ✅ NOUVEAU : Prix avec gestion PROMO */}
        <div className="product-price mb-3">
          {hasPromo && promoPrice ? (
            // Affichage avec promotion
            <div>
              <h5 className="text-success mb-0 fw-bold">
                {formatPriceLocal(promoPrice)}
              </h5>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted text-decoration-line-through" style={{ fontSize: '0.9rem' }}>
                  {formatPriceLocal(price)}
                </span>
                {discountPercentage && (
                  <Badge bg="danger" style={{ fontSize: '0.7rem' }}>
                    -{Math.round(discountPercentage)}%
                  </Badge>
                )}
              </div>
              {activePromotionName && (
                <small className="text-success d-block mt-1">
                  <Tag size={10} className="me-1" />
                  {activePromotionName}
                </small>
              )}
            </div>
          ) : (
            // Prix normal sans promotion
            <h5 className="text-primary mb-0">{formatPriceLocal(price)}</h5>
          )}
        </div>

        {/* Informations supplémentaires */}
        <div className="product-info mb-3">
          <div className="row g-0">
            {sku && (
              <div className="col-6">
                <small className="text-muted d-block">
                  <strong>SKU:</strong>
                </small>
                <small className="d-block" style={{ fontSize: "0.75rem" }}>
                  {sku}
                </small>
              </div>
            )}

            {barcode && (
              <div className="col-6">
                <small className="text-muted d-block">
                  <strong>Code-barres:</strong>
                </small>
                <small className="d-block" style={{ fontSize: "0.7rem" }}>
                  <code>
                    {barcode.length > 12
                      ? `${barcode.substring(0, 12)}...`
                      : barcode}
                  </code>
                </small>
              </div>
            )}
          </div>
        </div>

        {/* Alerte si pas de code-barres */}
        {!barcode && isManager && (
          <div className="mb-2">
            <Badge bg="warning" className="w-100 p-2">
              <AlertTriangle size={12} className="me-1" />
              <small>Aucun code-barres</small>
            </Badge>
          </div>
        )}

        {/* Actions en bas */}
        <div className="mt-auto">
          {barcode && (
            <Button
              variant="outline-primary"
              size="sm"
              className="w-100 mb-2"
              onClick={handleGenerateLabel}
            >
              <Printer size={14} className="me-1" />
              Générer étiquette
            </Button>
          )}
        </div>
      </Card.Body>

      {/* Styles */}
      <style jsx>{`
        .product-card {
          transition: all 0.2s ease-in-out;
          overflow: hidden;
          position: relative;
        }

        .product-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
        }

        .product-card.border-primary {
          border-width: 2px !important;
          background-color: rgba(13, 110, 253, 0.05);
        }

        .dropdown-toggle-custom {
          transition: all 0.2s ease-in-out;
        }

        .product-card:hover .dropdown-toggle-custom {
          opacity: 1 !important;
          transform: scale(1.05);
        }

        .dropdown-menu {
          z-index: 1050 !important;
        }

        .badge {
          font-size: 0.7rem;
          font-weight: 500;
        }

        .product-image-container {
          margin: -1px -1px 0 -1px;
          position: relative;
        }

        .product-image-container img {
          border-radius: 0 !important;
        }

        .position-absolute {
          pointer-events: auto;
        }
      `}</style>
    </Card>
  );
};

export default ProductCard;